<script setup lang="ts">
withDefaults(
  defineProps<{
    title?: string;
    breakpoint?: string;
    showArrow?: boolean;
    filters: Array<{
      name: string;
      legend: string;
      enabled: Set<string>;
      options: ReadonlyArray<{ title: string; tagName: string; value: string }>;
    }>;
  }>(),
  {
    title: undefined,
    breakpoint: undefined,
    showArrow: true,
  },
);
</script>

<template>
  <section>
    <h2
      v-if="title"
      class="text-center font-headline text-xl font-normal leading-[175%]! sm:text-3xl"
      v-text="title"
    />

    <!-- Desktop view -->
    <form class="desktop">
      <UiFilterCheckbox
        v-for="filter in filters"
        :key="filter.name"
        v-bind="filter"
      />
    </form>

    <!-- Mobile view -->
    <form class="mobile">
      <details v-for="{ legend, ...filter } in filters" :key="filter.name">
        <summary>{{ legend }}</summary>
        <UiFilterCheckbox v-bind="filter" />
      </details>
    </form>

    <UiFilterReset />

    <IconsArrowsDownLong v-if="showArrow" class="arrow" />
  </section>
</template>

<style scoped>
@reference "~/assets/styles/main.css";

section {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  &:has(.arrow) {
    margin-bottom: 2rem;
  }

  @media screen and (min-width: theme("screens.sm")) {
    gap: 3rem;

    &:has(.arrow) {
      /* To make room for the absolute positioned arrow */
      margin-bottom: 7rem;
    }
  }
}

.arrow {
  position: absolute;
  margin-inline: auto;
  inset: auto 0 -5rem;
  fill: var(--color-coral-pop);
  display: none;

  @media screen and (min-width: theme("screens.sm")) {
    display: block;
  }
}

form.desktop {
  display: none;
  justify-content: space-between;

  @media screen and (min-width: theme("screens.sm")) {
    display: flex;
  }

  fieldset {
    --padding-inline: 3rem;
    grid-template-rows: repeat(3, 1fr);
    grid-auto-flow: column;
    display: grid;

    flex: 1 1 auto;

    &:not(:last-of-type) {
      border-right: 1px solid var(--accent-color, var(--color-green-30));
      padding-right: var(--padding-inline);
    }

    &:not(:first-of-type) {
      padding-left: var(--padding-inline);
    }
  }
}

form.mobile {
  @media screen and (min-width: theme("screens.sm")) {
    display: none;
  }

  details {
    color: white;
    margin: 1rem auto;
    width: 100%;
    border-bottom: 1px solid var(--accent-color, var(--color-green-30));
    padding-bottom: 1rem;

    fieldset {
      margin: 1.5rem auto 2rem 0.5rem;
    }

    summary {
      width: 100%;
      position: relative;
      cursor: pointer;
      list-style: none;
      color: var(--filter-color, var(--color-seagreen-50));
      letter-spacing: 0.0625rem;

      @media screen and (max-width: theme("screens.xs")) {
        font-size: 0.75rem;
      }

      &::-webkit-details-marker {
        display: none;
      }

      &::after {
        content: "+";
        position: absolute;
        right: 0;
        margin-top: -0.25rem;
        font-size: 1.375rem;
        border: 1.5px solid var(--color-seagreen-50);
        border-radius: 100%;
        width: 1.5rem;
        height: 1.5rem;
        text-align: center;
        line-height: 1;
        margin-right: 0.5rem;
      }
    }

    &[open] {
      summary::after {
        content: "-";
      }
    }
  }
}
</style>
